import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/Nav";

/** Iter-42: self-serve "Set a new password" step 2. The reset link
 *  carries a `?token=…` query param. On submit we POST it with the new
 *  password and, on success, invite the user to sign in with the new
 *  credentials. All existing sessions for the account are invalidated
 *  server-side (token_version bumped). */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password",
                                       { token, new_password: password });
      setDone(data.email || "");
      toast.success("Password updated — please sign in.");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const missingToken = !token;

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="bg-white border border-[color:var(--border)] p-10">
          <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-2">§ Set new password</p>
          <h2 className="font-editorial text-3xl mb-4">Choose a new password</h2>

          {missingToken ? (
            <div data-testid="reset-missing-token">
              <p className="text-sm text-red-700">This reset link is missing its token. Ask your admin for a new link.</p>
              <p className="text-sm mt-6">
                <Link to="/forgot-password" className="link-underline text-[color:var(--text)]">
                  Request a new reset →
                </Link>
              </p>
            </div>
          ) : done !== null ? (
            <div data-testid="reset-success">
              <p className="text-sm">
                <strong>Password updated for {done || "your account"}.</strong> Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[color:var(--text-muted)] mb-6">
                Pick a strong password. Anywhere you were signed in previously will be signed out automatically.
              </p>
              <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest mb-1">New password</span>
                  <input
                    type="password" required minLength={6} value={password}
                    onChange={(e)=>setPassword(e.target.value)}
                    className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid="reset-password"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs uppercase tracking-widest mb-1">Confirm new password</span>
                  <input
                    type="password" required minLength={6} value={confirm}
                    onChange={(e)=>setConfirm(e.target.value)}
                    className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid="reset-confirm"
                  />
                </label>
                <button type="submit" disabled={busy || !password}
                        className="btn-primary w-full"
                        data-testid="reset-submit">
                  {busy ? "Saving…" : "Set new password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
